import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TelegramLogo, CheckCircle, PaperPlaneTilt, LinkSimpleBreak, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TelegramConnect() {
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState(null); // {code, deep_link}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/telegram/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false, linked: false }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startLink = async () => {
    setBusy(true);
    try {
      const r = await api.post("/telegram/link/start");
      setPending(r.data);
      if (r.data.deep_link) window.open(r.data.deep_link, "_blank", "noopener");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmLink = async () => {
    setBusy(true);
    try {
      await api.post("/telegram/link/confirm");
      toast.success("Telegram connected — alerts will be delivered here");
      setPending(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    try { await api.post("/telegram/test"); toast.success("Test message sent to your Telegram"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const unlink = async () => {
    if (!window.confirm("Disconnect Telegram? You'll stop receiving alerts there.")) return;
    try { await api.post("/telegram/unlink"); toast.success("Telegram disconnected"); setPending(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!status) return null;

  return (
    <div data-testid="telegram-connect" className="mt-4 border border-border bg-card p-4 rounded-sm">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <TelegramLogo size={22} weight="fill" className="text-[#2AABEE]" />
          <div>
            <div className="text-sm font-medium flex items-center gap-2">
              Telegram Alert Delivery
              {status.linked && <span className="mono text-[10px] uppercase px-2 py-0.5 rounded-sm bg-signal-green/15 text-signal-green flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Connected</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {!status.configured
                ? "Not yet available — the server admin needs to add a Telegram bot token."
                : status.linked
                  ? "Price, AI-score and strategy alerts are pushed to your Telegram."
                  : "Get your triggered alerts pushed straight to Telegram."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {status.configured && status.linked && (
            <>
              <Button data-testid="telegram-test-btn" onClick={sendTest} size="sm" variant="outline" className="rounded-sm border-border gap-1.5 h-8"><PaperPlaneTilt size={14} /> Send test</Button>
              <Button data-testid="telegram-unlink-btn" onClick={unlink} size="sm" variant="outline" className="rounded-sm border-border gap-1.5 h-8 text-muted-foreground"><LinkSimpleBreak size={14} /> Disconnect</Button>
            </>
          )}
          {status.configured && !status.linked && !pending && (
            <Button data-testid="telegram-connect-btn" onClick={startLink} disabled={busy} size="sm" className="rounded-sm bg-[#2AABEE] hover:bg-[#2AABEE]/90 text-white gap-1.5 h-8"><TelegramLogo size={15} weight="fill" /> {busy ? "Preparing…" : "Connect Telegram"}</Button>
          )}
        </div>
      </div>

      {status.configured && !status.linked && pending && (
        <div data-testid="telegram-link-steps" className="mt-4 border-t border-border/60 pt-4 text-sm">
          <ol className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2"><span className="mono text-volt">1.</span>
              <span>A new tab opened your bot <b className="text-foreground">@{pending.bot_username}</b>. Tap <b className="text-foreground">Start</b> there.
                {pending.deep_link && <a href={pending.deep_link} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex items-center gap-1 text-[#2AABEE] hover:underline">open bot <ArrowSquareOut size={12} /></a>}
              </span>
            </li>
            <li className="flex items-start gap-2"><span className="mono text-volt">2.</span>
              <span>Then click <b className="text-foreground">I've started the bot</b> below. (Your link code: <span className="mono text-foreground">{pending.code}</span>)</span>
            </li>
          </ol>
          <Button data-testid="telegram-confirm-btn" onClick={confirmLink} disabled={busy} className="mt-3 rounded-sm bg-signal-green hover:bg-signal-green/90 text-black gap-1.5 h-9"><CheckCircle size={16} weight="fill" /> {busy ? "Checking…" : "I've started the bot"}</Button>
        </div>
      )}
    </div>
  );
}
